import { LinkBreak } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { AgGridTable } from 'core/components';
import { useAgGridClientTableContext } from 'core/states';
import { groupByObjProp } from 'core/utils';
import { ImportVendorQuoteButton } from 'quotes/components';
import { VendorQuoteAttachToType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { useCustomerQuotePartsContext } from 'quotes/states';
import { ICustomerQuote, IVendorQuote } from 'quotes/types';
import { Color, Variant } from 'theme/enums';

import { AttachExistingVendorQuotesButton } from './AttachExistingVendorQuotesButton';

interface IProps {
  quote: ICustomerQuote;
}

export const AttachedVendorQuoteListTable = ({ quote }: IProps) => {
  const { t } = useTranslation();
  const { rows, selectedRows, updateRows } = useAgGridClientTableContext();
  const { setQuoteQuoteAssociation } = useCustomerQuotePartsContext();

  const { detachVendorQuote, reattachVendorQuote } = useQuoteActions();

  const handleImportComplete = (quoteId: number) => {
    setQuoteQuoteAssociation({
      quoteId: quote.id,
      vendorQuotes: [quoteId],
    });
  };

  const actionButtons = () => [
    <AttachExistingVendorQuotesButton
      key="attachExistingVendorQuotes"
      quote={quote}
      onAttachVendorQuoteSuccess={onAttachVendorQuoteSuccess}
    />,
    <ImportVendorQuoteButton
      key="importVendorQuote"
      quote={quote}
      refetchQueriesKeys={['attachedVendorQuotes']}
      onImportComplete={handleImportComplete}
      defaultValues={{
        attachTo: VendorQuoteAttachToType.JOB,
        project: {
          id: quote.vendorProjectId,
          name: quote.vendorProjectName,
        },
        jobName: quote.jobName,
      }}
    />,
  ];

  const selectionActions = () => [
    {
      label: t('buttonLabelDetach'),
      color: Color.DANGER,
      variant: Variant.SOFT,
      startDecorator: <LinkBreak />,
      onClick: onDetachVendorQuote,
      disabled: !selectedRows.some((row) => !row.quoteQuoteDisaffiliationId),
    },
    {
      label: t('buttonLabelReattach'),
      color: Color.SUCCESS,
      variant: Variant.SOLID,
      onClick: onReattachVendorQuote,
      disabled: !selectedRows.some((row) => row.quoteQuoteDisaffiliationId),
    },
  ];

  const onAttachVendorQuoteSuccess = (attachedQuotes: IVendorQuote[]) => {
    const attachedVendorQuotesGroupedByIds = groupByObjProp(rows, 'id');

    const { alreadyAttachedToProjectOrJob, newAttachedQuotes } = attachedQuotes.reduce(
      (acc, vendorQuote) => {
        if (attachedVendorQuotesGroupedByIds?.[vendorQuote?.id]) {
          acc.alreadyAttachedToProjectOrJob.push({
            ...attachedVendorQuotesGroupedByIds[vendorQuote.id],
            ...vendorQuote,
          });

          return acc;
        }

        acc.newAttachedQuotes.push(vendorQuote);

        return acc;
      },
      { alreadyAttachedToProjectOrJob: [], newAttachedQuotes: [] }
    );

    updateRows(
      {
        add: newAttachedQuotes,
        update: alreadyAttachedToProjectOrJob,
      },
      { goToRowIndex: rows?.length + newAttachedQuotes?.length - 1 }
    );

    setQuoteQuoteAssociation({
      quoteId: quote.id,
      vendorQuotes: attachedQuotes.map((quote) => quote.id),
    });
  };

  const onDetachVendorQuote = async () => {
    const filteredSelectedRows = selectedRows.filter((row) => !row.quoteQuoteDisaffiliationId);

    const { success } = await detachVendorQuote({
      quoteQuoteDisaffiliations: filteredSelectedRows.map((row) => ({
        quoteId: quote.id,
        detachedQuoteId: row.id,
      })),
    });

    setQuoteQuoteAssociation({
      quoteId: quote.id,
      vendorQuotes: filteredSelectedRows.map((row) => row.id),
    });

    return success;
  };

  const onReattachVendorQuote = async () => {
    const filteredSelectedRows = selectedRows.filter((row) => !!row.quoteQuoteDisaffiliationId);

    const { success } = await reattachVendorQuote({
      ids: filteredSelectedRows.map((row) => row.quoteQuoteDisaffiliationId),
    });

    setQuoteQuoteAssociation({
      quoteId: quote.id,
      vendorQuotes: filteredSelectedRows.map((row) => row.id),
    });

    return success;
  };

  return <AgGridTable actionButtons={actionButtons} selectionActions={selectionActions} />;
};
